import CDialBase from './Dials'
import { PlaceInNestedAkixiNameSpace } from '../utils/Utils'
import { EaseInOutQuad } from '../../EasingFunctions'

/**
 * This class draws a new circular dial. It will have the following default styling:
 * Backgound Colour: Akixi Blue (#005497)
 * Foreground Colour: White
 * Reverse Gauge Face: False
 * Reverse Gauge Direction: False
 *
 * Note: Reverse Gauge Face makes no change with this dial face due to its symmetry.
 *
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */

class CProgressRing extends CDialBase {
    /**
     * Private method.
     *
     * Draws the dial on the canvas.
     *
     * @param {number} [flStopValue] Optional - The value at which the dial ends,
     * defaults to the current dial value.
     * @param {number} [flStartValue] Optional - The value at which the dial
     * starts, defaults to the stop value.
     * @param {number} [flFractionComplete=1.0] Optional - How far through the
     * animation we are, defaults to 100%.
     */
    protected Draw(
        flStopValue: number = this.m_flCurrentValue,
        flStartValue: number = flStopValue,
        flFractionComplete = 1.0
    ): void {
        const p2DContext = this.m_pCanvas.getContext('2d') as CanvasRenderingContext2D
        const iBoxSize = Math.min(this.m_iWidth, this.m_iHeight)
        const flRadius = (iBoxSize * 0.85) / 2
        const flDialWidth = flRadius * 0.1
        const flOuterLineThickness = 0.1 * flDialWidth
        const flProgressLineWidth = flOuterLineThickness * 2
        let flFontSize = 40
        const flMidHeight = this.m_iHeight / 2

        /*
         * Draw the outer dial elements
         */
        p2DContext.fillStyle = this.m_sBackgroundColour
        p2DContext.fillRect(0, 0, this.m_iWidth, this.m_iHeight)

        p2DContext.strokeStyle = this.m_sForegroundColour
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.lineWidth = flOuterLineThickness
        p2DContext.beginPath()
        p2DContext.arc(this.m_iWidth / 2, flMidHeight, flRadius, 0, 2 * Math.PI)
        p2DContext.stroke()

        p2DContext.beginPath()
        p2DContext.arc(this.m_iWidth / 2, flMidHeight, flRadius - flDialWidth, 0, 2 * Math.PI)
        p2DContext.stroke()

        /*
         * Draw the centre text.
         */
        const flDisplayValue = EaseInOutQuad(flFractionComplete) * (flStopValue - flStartValue) + flStartValue
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.textAlign = 'center'
        p2DContext.textBaseline = 'bottom'
        const pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)
        const flWidthRatio = (flRadius * 1.5) / pTextDimensions.width
        const flHeightRatio = (flRadius * 1.2) / flFontSize
        flFontSize *= Math.min(flHeightRatio, flWidthRatio)
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.fillText(this.m_sDisplayValue, this.m_iWidth / 2, flMidHeight + (1.1 * flFontSize) / 2)

        /*
         * Draw the progress bar.
         */
        const flUsableFraction = flDisplayValue / this.m_flRange
        let flEndPosition = flUsableFraction * 2 * Math.PI + (3 * Math.PI) / 2
        if (this.m_bReverseDirection) {
            flEndPosition = (3 * Math.PI) / 2 - flUsableFraction * 2 * Math.PI
        }

        p2DContext.lineWidth = flProgressLineWidth
        p2DContext.beginPath()
        p2DContext.lineCap = 'round'

        const flStartAngle = (3 * Math.PI) / 2
        const flEndAngle = flEndPosition
        p2DContext.arc(
            this.m_iWidth / 2,
            flMidHeight,
            flRadius - flDialWidth / 2,
            flStartAngle,
            flEndAngle,
            this.m_bReverseDirection
        )
        p2DContext.stroke()
    }
}

export default CProgressRing
PlaceInNestedAkixiNameSpace('Dials', 'CProgressRing', CProgressRing)
